<template>
  <form-wrapper
    :target="getId"
    :inline="inline"
    :icon="icon"
    :icon-classes="iconClasses"
    :underlined="underlined"
    :label-classes="labelClasses"
    :label="label"
    :error="error"
    :required="required"
    :has-value="!isValueEmpty"
    @label-click="onLabelClick"
    v-click-outside.native="close"
  >
    <select
      ref="select"
      v-model="model"
      class="form-field appearance-none"
      :name="name"
      :class="[fieldClasses, getFieldClasses]"
      :tabindex="isMobile() || !dropdown ? 0 : -1"
      :disabled="disabled"
      :id="getId"
      :required="required"
    >
      <option value v-if="placeholder">{{ placeholder }}</option>
      <option
        :value="getValue(option)"
        v-for="(option, index) in options"
        :key="index"
      >{{ getLabel(option) }}</option>
    </select>
    <template v-if="!disabled && dropdown && !isMobile()">
      <button
        ref="button"
        class="absolute inset-0 w-full cursor-default form-field bg-transparent focus:bg-transparent hover:bg-transparent focus:outline-none"
        :class="getFieldClasses"
        @keydown="onKeydown"
        @focus="onButtonFocus"
        @click="onButtonClick"
      ></button>
      <transition name="translate-y">
        <div
          v-if="dropdownOpen"
          class="absolute top-100 left-0 w-full cursor-pointer shadow-xl bg-white py-2 z-10 duration-150"
        >
          <ul ref="list" class="dropdown overflow-y-auto cursor-pointer">
            <li
              class="py-1 px-4 hover:bg-gray-100"
              v-if="placeholder"
              :class="{
                    'bg-gray-300 hover:bg-gray-300': focusedIndex === -1,
                    'font-bold': value === ''
                  }"
              @click="setValue(null)"
            >{{ placeholder }}</li>
            <li
              v-for="(option, index) in options"
              class="py-1 px-4 hover:bg-gray-100"
              :key="index"
              :class="{
                    'bg-gray-300 hover:bg-gray-300': isFocused(index),
                    'font-bold': value === getValue(option)
                  }"
              @click="setValue(option)"
            >{{ getLabel(option) }}</li>
          </ul>
        </div>
      </transition>
    </template>
    <template #label><slot name="label"></slot></template>
    <template #icon><slot name="icon"></slot></template>
    <template #action><slot name="caret"><span class="form-caret text-gray-500 pointer-events-none"></span></slot></template>
  </form-wrapper>
</template>

<script>
import ClickOutside from "vue-click-outside";
import { resolveValue, isMobile } from "../../helpers/utils";
import FormWrapper from "./FormWrapper";

export default {
  components: {
    FormWrapper
  },
  model: {
    event: "change"
  },
  data() {
    return {
      dropdownOpen: false,
      focusedIndex: 0
    };
  },
  props: {
    label: String,
    value: [String, Number],
    placeholder: String,
    name: String,
    icon: String,
    fieldClasses: [String, Array],
    labelClasses: [String, Array],
    iconClasses: [String, Array],
    options: {
      type: Array,
      default: () => []
    },
    valueGetter: {
      type: String,
      default: "value"
    },
    labelGetter: {
      type: String,
      default: "label"
    },
    error: [String, Array],
    underlined: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    dropdown: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isValueEmpty() {
      return _.isEmpty(this.model)
    },
    getFieldClasses() {
      return {
        "is-underlined": this.underlined,
        "border-danger focus:border-danger": this.error,
        "has-icon": this.icon,
        "is-placeholder": !this.model
      };
    },
    getId() {
      return this.name ? `${this.name}-${this._uid}` : this._uid;
    },
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("change", value);
        this.close();
      }
    }
  },
  methods: {
    getValue(option) {
      return typeof option === "string"
        ? option
        : resolveValue(option, this.valueGetter);
    },
    getLabel(option) {
      return typeof option === "string"
        ? option
        : resolveValue(option, this.labelGetter);
    },
    setValue(option) {
      this.model = option ? this.getValue(option) : "";
    },
    onKeydown(event) {
      switch (event.keyCode) {
        case 9:
          if (this.focusedIndex < this.options.length - 1) {
            event.preventDefault();
            this.focusedIndex++;
          } else {
            this.close();
          }
          break;
        case 13:
          this.setValue(this.options[this.focusedIndex]);
          this.close();
          event.target.blur();
          break;
        case 38:
          event.preventDefault();
          if (this.focusedIndex > (this.placeholder ? -1 : 0)) {
            this.focusedIndex--;
          }
          break;
        case 40:
          event.preventDefault();
          if (this.focusedIndex < this.options.length - 1) {
            this.focusedIndex++;
          }
          break;
        default:
          for (let index = 0; index < this.options.length; index++) {
            if (
              this.getLabel(this.options[index])
                .toLowerCase()
                .startsWith(event.key)
            ) {
              this.focusedIndex = index;
              break;
            }
          }
          break;
      }
      this.scrollIntoList();
    },
    onButtonFocus() {
      setTimeout(() => {
        this.open()
      }, 100)
    },
    onButtonClick() {
      this.toggle()
    },
    isFocused(index) {
      return this.focusedIndex === index;
    },
    scrollIntoList() {
      const list = this.$refs.list;
      const activeChild =
        list.children[
          this.placeholder ? this.focusedIndex + 1 : this.focusedIndex
        ];
      list.scrollTop = activeChild.offsetTop - list.offsetTop;
    },
    onLabelClick() {
      if (!this.disabled && this.dropdown && !this.isMobile()) {
        this.$refs.button.focus();
      } else {
        this.$refs.select.focus();
      }
    },
    open() {
      this.focusedIndex = 0;
      this.dropdownOpen = true;
    },
    close() {
      this.dropdownOpen = false;
    },
    toggle() {
      if (this.dropdownOpen) {
        this.close()
      } else {
        this.open()
      }
    },
    isMobile
  },
  directives: {
    ClickOutside
  }
};
</script>

<style>
.translate-y-enter, .translate-y-leave-to {
  @apply transform -translate-y-2 opacity-0;
}
select {
  background-color: inherit;
  border-radius: inherit;
}
</style>
